'use client';

/**
 * SearchProgress — three-row status panel rendered while the streaming
 * /api/advocate-search call is in flight. The parent flips each court's
 * state to 'searching' as soon as the `started` event arrives, then to
 * 'done' / 'error' on each `court_done` event.
 *
 * Copy is deliberately neutral — no mention of scraping, relays, or exact
 * infra (per CLAUDE.md blog rules).
 */

import { COURT_LABELS, type AdvocateCourt, type CourtProgressMap, type CourtReason } from '../types';

interface SearchProgressProps {
  progress: CourtProgressMap;
  queryName: string;
  city: string;
}

const ORDER: AdvocateCourt[] = ['district', 'high_court', 'supreme_court', 'tribunal'];

function Spinner() {
  return (
    <svg
      className="h-5 w-5 animate-spin text-primary-600"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
      <path
        className="opacity-90"
        fill="currentColor"
        d="M4 12a8 8 0 018-8v3a5 5 0 00-5 5H4z"
      />
    </svg>
  );
}

function Check() {
  return (
    <svg className="h-5 w-5 text-india-green-600" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
      <path
        fillRule="evenodd"
        d="M16.704 5.29a1 1 0 010 1.42l-8 8a1 1 0 01-1.42 0l-4-4a1 1 0 111.42-1.42L8 12.59l7.29-7.3a1 1 0 011.415 0z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function Cross() {
  return (
    <svg className="h-5 w-5 text-ink-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
      <path
        fillRule="evenodd"
        d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function Dot() {
  return <span className="block h-2 w-2 rounded-full bg-ink-5" aria-hidden="true" />;
}

export default function SearchProgress({ progress, queryName, city }: SearchProgressProps) {
  return (
    <section aria-label="Search progress" className="rounded-xl border border-line bg-paper-elev p-6 sm:p-8">
      <p className="eyebrow mb-3">Searching cases</p>
      <h2 className="text-xl sm:text-2xl font-serif text-ink mb-6">
        Looking up <span className="italic">{queryName}</span>
        {city && <> in <span className="italic">{toTitleCase(city)}</span></>}
      </h2>

      <ul className="divide-y divide-line-soft">
        {ORDER.map(court => {
          const p = progress[court];
          const label = COURT_LABELS[court];
          return (
            <li key={court} className="flex items-center gap-4 py-3.5">
              <div className="flex h-6 w-6 items-center justify-center">
                {p.status === 'pending' && <Dot />}
                {p.status === 'searching' && <Spinner />}
                {p.status === 'done' && <Check />}
                {p.status === 'error' && <Cross />}
              </div>
              <div className="flex-1 text-sm">
                <div className="font-medium text-ink">{label}</div>
                <div className="mt-0.5 text-xs text-ink-4">
                  {p.status === 'pending' && 'Queued'}
                  {p.status === 'searching' && 'Checking for your cases…'}
                  {p.status === 'done' && (
                    <>
                      {typeof p.count === 'number' ? `${p.count} case${p.count === 1 ? '' : 's'} found` : 'Done'}
                      {p.reason && <span className="text-ink-4"> · {friendlyReason(p.reason)}</span>}
                    </>
                  )}
                  {p.status === 'error' && friendlyReason(p.reason)}
                </div>
              </div>
              {p.status === 'done' && typeof p.count === 'number' && p.count > 0 && (
                <span className="mono text-sm text-primary-800 tabular-nums">{p.count}</span>
              )}
            </li>
          );
        })}
      </ul>

      <p className="mt-6 text-xs text-ink-4">
        This runs across the official case records — it usually finishes in under a minute.
      </p>
    </section>
  );
}

function friendlyReason(reason?: CourtReason): string {
  switch (reason) {
    case 'timeout':
      return 'Took longer than expected — we will retry.';
    case 'captcha':
    case 'upstream_unavailable':
    default:
      return "We couldn't reach this court just now — we'll retry when you claim your cases.";
  }
}

function toTitleCase(s: string): string {
  return s.replace(/\b\w/g, c => c.toUpperCase());
}
